import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn, FILE_TYPE_MAPPING } from "@/lib/utils";
import { getErrorMessage } from "@/lib/api-error";
import {
  AlertCircle,
  Check,
  ChevronsUpDown,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { docsendApi } from "@/api/docsend-api";
import { useUser } from "@clerk/clerk-react";

// DocSend Icon Component
const DocSendIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <rect width="24" height="24" rx="4" fill="#5D5FEF" />
    <path
      d="M7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z"
      fill="white"
    />
  </svg>
);

interface ImportedFile {
  _id: string;
  file_name: string;
  file_type: string;
  file_url: string;
  file_size: string;
}

interface DocSendCredentials {
  url: string;
  email: string;
  passcode?: string;
  fileType?: string;
}

interface DocSendPickerProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  opportunityId?: string;
  onImportComplete?: (file: ImportedFile) => void;
  onImportError?: () => void;
  /** For "collect" mode - callback when user submits credentials without importing */
  onCredentialsCollect?: (credentials: DocSendCredentials) => void;
  /** "import" = import file immediately, "collect" = just collect credentials */
  mode?: "import" | "collect";
  defaultEmail?: string;
  defaultFileType?: string;
  /** Pre-fills the URL, e.g. a deck link found in an inbound email. */
  defaultUrl?: string;
}

// File type options from FILE_TYPE_MAPPING
const FILE_TYPE_OPTIONS = Object.entries(FILE_TYPE_MAPPING).map(
  ([label, value]) => ({
    label,
    value,
  })
);

/**
 * Check if an error message likely indicates a password-protected document
 */
const isPasswordRelatedError = (message: string): boolean => {
  const passwordIndicators = [
    "invalid pdf",
    "conversion failed",
    "access denied",
    "password",
    "passcode",
    "protected",
    "unauthorized",
  ];
  const lowerMessage = message.toLowerCase();
  return passwordIndicators.some((indicator) => lowerMessage.includes(indicator));
};

export function DocSendPicker({
  open,
  onClose,
  companyId,
  opportunityId,
  onImportComplete,
  onImportError,
  onCredentialsCollect,
  mode = "import",
  defaultEmail,
  defaultFileType,
  defaultUrl,
}: DocSendPickerProps) {
  const { user } = useUser();

  // Form state
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState(defaultEmail || "");
  const [passcode, setPasscode] = useState("");
  const [fileType, setFileType] = useState(defaultFileType || "");
  const [fileTypeOpen, setFileTypeOpen] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightPasscode, setHighlightPasscode] = useState(false);

  // Seed a URL handed in by the caller (a deck link found in an email). Keyed
  // on `open` so re-opening the dialog restores it after the reset below.
  useEffect(() => {
    if (open && defaultUrl) setUrl(defaultUrl);
  }, [open, defaultUrl]);

  // Pre-fill email from user context
  useEffect(() => {
    if (open && !email && user?.primaryEmailAddress?.emailAddress) {
      setEmail(user.primaryEmailAddress.emailAddress);
    }
  }, [open, user, email]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setUrl(defaultUrl || "");
      setPasscode("");
      setError(null);
      setHighlightPasscode(false);
      setFileType(defaultFileType || "");
      // Don't reset email - keep it for convenience
    }
  }, [open, defaultFileType, defaultUrl]);

  const validateUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.includes("docsend.com");
    } catch {
      return false;
    }
  };

  const handleUrlBlur = () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return;

    if (!validateUrl(trimmedUrl)) {
      setError("Please enter a valid DocSend URL");
      return;
    }

    // Detect customized DocSend links (e.g., docsend.com/v/brand/docname)
    if (/docsend\.com\/v\/[^/]+\/.+/.test(trimmedUrl) && !/docsend\.com\/view\//.test(trimmedUrl)) {
      const customUrlMessage = "Customized DocSend links are not currently supported. Please use the standard share link (e.g., docsend.com/view/abc123).";
      setError(customUrlMessage);
      toast.error(customUrlMessage);
      return;
    }

    // Clear error if URL is now valid
    setError(null);
  };

  const handleSubmit = async () => {
    // Validate inputs
    if (!url.trim()) {
      setError("Please enter a DocSend URL");
      return;
    }

    if (!validateUrl(url)) {
      setError("Please enter a valid DocSend URL");
      return;
    }

    // Detect customized DocSend links (e.g., docsend.com/v/brand/docname)
    if (/docsend\.com\/v\/[^/]+\/.+/.test(url) && !/docsend\.com\/view\//.test(url)) {
      setError(
        "Customized DocSend links are not currently supported. Please use the standard share link (e.g., docsend.com/view/abc123)."
      );
      return;
    }

    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    if (!fileType) {
      setError("Please select a document type");
      return;
    }

    setError(null);

    // In "collect" mode, just return the credentials without importing
    if (mode === "collect") {
      if (onCredentialsCollect) {
        onCredentialsCollect({
          url: url.trim(),
          email: email.trim(),
          passcode: passcode.trim() || undefined,
          fileType: fileType || undefined,
        });
      }
      onClose();
      return;
    }

    // In "import" mode, actually import the file
    setLoading(true);

    try {
      const response = await docsendApi.importFile({
        url: url.trim(),
        email: email.trim(),
        passcode: passcode.trim() || undefined,
        company_id: companyId,
        opportunity_id: opportunityId,
        file_type: fileType || undefined,
      });

      toast.success("File imported successfully from DocSend");

      if (onImportComplete && response.file) {
        // Use the selected file type label as the file name (e.g., "Pitch Deck.pdf")
        // instead of the random hash from DocSend
        const fileExtension = response.file.file_name?.split('.').pop() || 'pdf';
        const displayName = selectedFileTypeLabel
          ? `${selectedFileTypeLabel}.${fileExtension}`
          : response.file.file_name;

        onImportComplete({
          _id: response.file._id,
          file_name: displayName,
          file_type: response.file.file_type,
          file_url: response.file.file_url,
          file_size: response.file.file_size,
        });
      }

      onClose();
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      onImportError?.();

      // Check if error might be due to missing password
      if (isPasswordRelatedError(errorMessage) && !passcode.trim()) {
        const passwordMessage = "This document may be password-protected. Please enter the passcode and try again.";
        setError(passwordMessage);
        setHighlightPasscode(true);
      } else {
        setError(errorMessage);
        setHighlightPasscode(false);
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedFileTypeLabel = FILE_TYPE_OPTIONS.find(
    (opt) => opt.value === fileType
  )?.label;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DocSendIcon className="h-5 w-5" />
            Import from DocSend
          </DialogTitle>
          <DialogDescription>
            Enter the DocSend link and your email to import the document.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* DocSend URL */}
          <div className="space-y-2">
            <Label htmlFor="docsend-url">
              DocSend URL <span className="text-destructive">*</span>
            </Label>
            <Input
              id="docsend-url"
              placeholder="https://docsend.com/view/abc123"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={handleUrlBlur}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              Paste the DocSend link you want to import
            </p>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="docsend-email">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="docsend-email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              DocSend requires an email for document access
            </p>
          </div>

          {/* Passcode (optional) */}
          <div className="space-y-2">
            <Label htmlFor="docsend-passcode">
              Passcode <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="docsend-passcode"
              type="password"
              placeholder="Enter passcode if required"
              value={passcode}
              onChange={(e) => {
                setPasscode(e.target.value);
                setHighlightPasscode(false);
              }}
              disabled={loading}
              className={cn(
                highlightPasscode && "ring-2 ring-orange-500 border-orange-500"
              )}
            />
            <p className={cn(
              "text-xs",
              highlightPasscode ? "text-orange-600 font-medium" : "text-muted-foreground"
            )}>
              {highlightPasscode
                ? "This document appears to require a passcode"
                : "Some DocSend links require a passcode for access"}
            </p>
          </div>

          {/* File Type Selection */}
          <div className="space-y-2">
            <Label>
              Document Type <span className="text-destructive">*</span>
            </Label>
            <Popover open={fileTypeOpen} onOpenChange={setFileTypeOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={fileTypeOpen}
                  className="w-full justify-between"
                  disabled={loading}
                >
                  {selectedFileTypeLabel || "Select document type..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search document type..." />
                  <CommandList>
                    <CommandEmpty>No document type found.</CommandEmpty>
                    <CommandGroup>
                      {FILE_TYPE_OPTIONS.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.label}
                          onSelect={() => {
                            setFileType(option.value);
                            setFileTypeOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              fileType === option.value
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          {option.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">
              Classify the document for better organization
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <ExternalLink className="mr-2 h-4 w-4" />
                Import
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DocSendPicker;
